/**
 * Retrieval progress SSE connections using @microsoft/fetch-event-source
 *
 * - Refresh (FULL_ORG_REFRESH | METADATA_TYPE): GET /api/retrievals/:id/refresh
 * - Initial (INITIAL_RETRIEVAL): GET /api/retrievals/:id/progress
 */

import { fetchEventSource } from '@microsoft/fetch-event-source';
import { env } from '@/config/env';
import { getAuthToken } from '../axios';
import { useAuthStoreV1 } from '@/stores/authStore_v1';
import { getDefaultOrgId } from '@/utils/defaultOrg';
import type { RefreshProgressData, RetrievalRefreshErrorData } from './types';
import type { InitialProgressData, RetrievalCompleteData } from './types';

export type RetrievalSSEEventType = 'connected' | 'progress_update' | 'phase_transition' | 'complete' | 'refresh_error' | 'stream_closed';

export interface RetrievalRefreshEventMap {
  connected: { message?: string; status: string; retrieval_id: string; retrieval_type: string };
  progress_update: RefreshProgressData;
  complete: RetrievalCompleteData;
}

export interface RetrievalInitialEventMap {
  connected: { message?: string; status: string; retrieval_id: string; retrieval_type: string };
  progress_update: InitialProgressData;
  phase_transition: { timestamp?: string; retrieval_id?: string; from_phase?: string; to_phase?: string };
  complete: RetrievalCompleteData;
}

export interface RetrievalConnectionHandlers<T = unknown> {
  onEvent?: (event: RetrievalSSEEventType, data: T) => void;
  onOpen?: () => void;
  onClose?: () => void;
  onError?: (err: unknown) => void;
  onUnauthenticated?: (data: RetrievalRefreshErrorData) => void;
}

export interface RetrievalConnection {
  abort: () => void;
}

function buildUrl(path: string): string {
  const base = env.API_URL.replace(/\/$/, '');
  const p = path.startsWith('/') ? path : `/${path}`;
  return `${base}${p}`;
}

function readBrowserOrgId(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const stored = window.localStorage.getItem('org_id');
    if (stored) return stored;
    const cookie = document.cookie
      .split(';')
      .map((part) => part.trim())
      .find((part) => part.startsWith('org_id='));
    return cookie ? decodeURIComponent(cookie.slice('org_id='.length)) : null;
  } catch {
    return null;
  }
}

function getHeaders(): Record<string, string> {
  const token = getAuthToken();
  const state = useAuthStoreV1.getState();
  const orgId =
    state.selectedOrg?.id ||
    getDefaultOrgId() ||
    readBrowserOrgId() ||
    (state.organizations.length === 1 ? state.organizations[0].id : null);
  const headers: Record<string, string> = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  if (orgId) headers['X-Organization-ID'] = orgId;
  if (state.user?.id) headers['X-User-ID'] = state.user.id;
  return headers;
}

/**
 * Connect to retrieval refresh SSE stream (FULL_ORG_REFRESH or METADATA_TYPE).
 * Same endpoint and event shape for both.
 */
export function createRetrievalRefreshConnection(
  retrievalId: string,
  handlers: RetrievalConnectionHandlers<RefreshProgressData | RetrievalCompleteData>
): RetrievalConnection {
  const controller = new AbortController();
  const url = buildUrl(`retrievals/${retrievalId}/refresh`);

  fetchEventSource(url, {
    method: 'GET',
    headers: getHeaders(),
    signal: controller.signal,
    openWhenHidden: true,
    async onopen(res) {
      if (!res.ok) {
        throw new Error(`SSE HTTP ${res.status}: ${res.statusText}`);
      }
      handlers.onOpen?.();
    },
    onmessage(ev) {
      try {
        const eventType = (ev.event || 'message') as RetrievalSSEEventType;
        const data = JSON.parse(ev.data || '{}');

        if (eventType === 'refresh_error' && (data as RetrievalRefreshErrorData).error === 'unauthenticated') {
          handlers.onUnauthenticated?.(data as RetrievalRefreshErrorData);
          return;
        }

        handlers.onEvent?.(eventType, data);
      } catch (e) {
        handlers.onError?.(e);
      }
    },
    onclose() {
      handlers.onClose?.();
    },
    onerror(err) {
      handlers.onError?.(err);
      // Fatal client errors (4xx) — no point retrying
      if (err instanceof Error && /SSE HTTP 4\d{2}/.test(err.message)) {
        throw err;
      }
      // Transient errors (network blips, 5xx, tab hidden) — let fetch-event-source auto-retry
    },
  }).catch((err) => {
    if (err?.name === 'AbortError') return;
    handlers.onError?.(err);
  });

  return {
    abort: () => controller.abort(),
  };
}

/**
 * Connect to retrieval initial progress SSE stream (INITIAL_RETRIEVAL).
 * Different endpoint and event shape (phase_transition, phase_breakdown, etc.).
 */
export function createRetrievalInitialConnection(
  retrievalId: string,
  handlers: RetrievalConnectionHandlers<InitialProgressData | RetrievalCompleteData>
): RetrievalConnection {
  const controller = new AbortController();
  const url = buildUrl(`retrievals/${retrievalId}/progress`);

  fetchEventSource(url, {
    method: 'GET',
    headers: getHeaders(),
    signal: controller.signal,
    openWhenHidden: true,
    async onopen(res) {
      if (!res.ok) {
        throw new Error(`SSE HTTP ${res.status}: ${res.statusText}`);
      }
      handlers.onOpen?.();
    },
    onmessage(ev) {
      try {
        const eventType = (ev.event || 'message') as RetrievalSSEEventType;
        const data = JSON.parse(ev.data || '{}');
        handlers.onEvent?.(eventType, data);
      } catch (e) {
        handlers.onError?.(e);
      }
    },
    onclose() {
      handlers.onClose?.();
    },
    onerror(err) {
      handlers.onError?.(err);
      if (err instanceof Error && /SSE HTTP 4\d{2}/.test(err.message)) {
        throw err;
      }
    },
  }).catch((err) => {
    if (err?.name === 'AbortError') return;
    handlers.onError?.(err);
  });

  return {
    abort: () => controller.abort(),
  };
}
